/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { applySnapshot, flow, getIdentifier, getParent } from 'mobx-state-tree';
import api from '~/api'; // used by fetch, destroy
import type { IThreadsModel } from '~/mst/models/noesis/threads/model';
import type { IThreadModel } from './model';
import { ToolExecutionStatus } from './message';

const actions = (self: IThreadModel) => ({
  fetch: flow(function* () {
    try {
      self.startFetching();
      const { data } = yield api.noesis.fetchThread(getIdentifier(self)!);
      applySnapshot(self, data);
      self.finishFetching();
    } catch (e) {
      self.failFetching(e);
    }
  }),
  destroy: flow(function* destroy() {
    self.startSyncing();
    try {
      yield api.noesis.deleteThread(getIdentifier(self)!);
      const threads = getParent<IThreadsModel>(self, 2);
      threads.remove(self);
      self.finishSyncing();
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  }),
  rename: flow(function* rename(name: string) {
    self.startSyncing();
    try {
      const { data } = yield api.noesis.updateThread(getIdentifier(self)!, { name });
      self.name = data.name;
      self.finishSyncing();
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  }),
  setPinned: flow(function* setPinned(pinned: boolean) {
    self.startSyncing();
    try {
      const { data } = yield api.noesis.updateThread(getIdentifier(self)!, { pinned });
      self.pinned = data.pinned;
      self.pinned_at = data.pinned_at ?? null;
      self.finishSyncing();
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  }),
  /**
   * Add a user message to the local state optimistically.
   * Returns the temporary ID so the caller can send it to the server via
   * WebSocket and reconcile when the server emits a message_confirmed event.
   */
  addOptimisticMessage(content: string, attachments?: { file_id: string; filename: string }[]): string {
    const tempId = `temp_${Math.random().toString(36).substr(2, 9)}`;
    self.messages.push({
      id: tempId,
      type: 'human' as const,
      content,
      timestamp: new Date().toISOString(),
      toolExecution: null,
      attachments: attachments || []
    });
    return tempId;
  },

  // Message Management
  addAssistantMessage(content: string, visualizationData?: any) {
    const message = {
      type: 'ai' as const,
      content,
      timestamp: new Date().toISOString(),
      metadata: {},
      visualizationData: visualizationData || null,
      toolExecution: null
    };

    self.messages.push(message);
  },

  addAssistantMessageWithTool(content: string = '', toolExecution?: any) {
    const messageId = `dummy_${Math.random().toString(36).substr(2, 9)}`;
    const message = {
      id: messageId,
      type: 'ai' as const,
      content,
      timestamp: new Date().toISOString(),
      visualizationData: null,
      toolExecution: toolExecution || null
    };

    self.messages.push(message);
    return messageId;
  },

  // Streaming Content Management - for assistant_delta events
  appendMessageContent(messageId: string, content: string) {
    const message = self.messages.find((m: any) => m.id === messageId);
    if (message) {
      message.content += content;
    }
  },

  // Final Content Update - for assistant_final events
  updateMessageWithFinalContent(messageId: string, content: string) {
    const message = self.messages.find((m: any) => m.id === messageId);
    if (message) {
      message.content = content;
    }
  },

  // Tool Execution Management
  setToolExecution(messageId: string, toolExecution: any) {
    const message = self.messages.find((m: any) => m.id === messageId);
    if (message) {
      message.toolExecution = toolExecution;
    }
  },

  updateToolStatus(messageId: string, status: ToolExecutionStatus, description?: string) {
    const message = self.messages.find((m: any) => m.id === messageId);
    if (message && message.toolExecution) {
      message.updateToolExecution({
        status,
        description: description || undefined
      });
    }
  },

  /**
   * Optimistically mark the thread as stopped in local state.
   * The actual stop signal is sent over the WebSocket by use_assistant.ts.
   */
  stopLocally() {
    const activeMessage = self.messages
      .slice()
      .reverse()
      .find((m: any) => m.type === 'ai' && m.toolExecution?.status === 'executing');

    if (activeMessage) {
      activeMessage.updateToolExecution({ status: 'stopped', description: 'Execution stopped' });
    }

    self.setStatus('stopped');
  },

  regenerateMessage(messageId: string): string {
    const messageIndex = self.messages.findIndex((m: any) => m.id === messageId);
    if (messageIndex === -1) throw new Error('Message not found');

    let humanMessageId: string | null = null;
    let humanMessageIndex: number | null = null;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (self.messages[i].type === 'human') {
        humanMessageId = self.messages[i].id;
        humanMessageIndex = i;
        break;
      }
    }

    if (!humanMessageId || humanMessageIndex === null) {
      throw new Error('No human message found before this AI message');
    }

    self.messages.splice(humanMessageIndex + 1);
    self.setStatus('processing');
    return humanMessageId;
  },

  updateUserMessage(messageId: string, content: string): void {
    const message = self.messages.find((m: any) => m.id === messageId);
    if (message) message.setContent(content);

    const messageIndex = self.messages.findIndex((m: any) => m.id === messageId);
    if (messageIndex !== -1) self.messages.splice(messageIndex + 1);

    self.setStatus('processing');
  },

  updateMessageId(tempId: string, realId: string) {
    try {
      // Update the message id in state
      const message = self.messages.find((m: any) => m.id === tempId);
      if (message) {
        // Simply update the id property directly
        message.id = realId;
      }
    } catch (error: any) {
      console.error('Error updating message id:', error);
      throw error;
    }
  },

  /**
   * Fill in a voice message's transcript once the backend's `transcript` event
   * arrives. Keyed by temp_message_id since transcription can complete before
   * (or regardless of) message_confirmed reassigning the real id.
   */
  setContentByTempId(tempId: string, content: string) {
    const message = self.messages.find((m: any) => m.id === tempId);
    if (message) message.setContent(content);
  },

  setStatus(status: string) {
    self.status = status;
  },

  setName(name: string) {
    self.name = name;
  },

  setNamePending(pending: boolean) {
    self.namePending = pending;
  },

  setProcessingPhase(phase: 'thinking' | 'responding' | 'transcribing' | null) {
    self.processingPhase = phase;
  },

  setTokenUsage(used: number, limit?: number) {
    self.total_tokens = used;
    if (limit) self.token_limit = limit;
  },

  setLimitReached(reached: boolean) {
    self.limitReached = reached;
  },

  spliceMessages(fromIndex: number) {
    self.messages.splice(fromIndex);
  },

  // Load the external files currently attached to this thread from the server.
  loadFiles: flow(function* loadFiles() {
    try {
      const { data } = yield api.noesis.listThreadFiles(getIdentifier(self)!);
      self.attachedFiles.replace(data.files || []);
    } catch (error) {
      // Non-fatal: a missing/empty file list should never break the chat.
      console.error('Failed to load thread files:', error);
    }
  }),

  // Upload an external data file (CSV/XLSX) and attach it to this thread.
  uploadFile: flow(function* uploadFile(file: File) {
    self.uploadingCount += 1;
    try {
      const { data } = yield api.noesis.uploadThreadFile(getIdentifier(self)!, file);
      self.attachedFiles.replace(data.files || []);
      return data.file;
    } catch (error) {
      self.failSyncing(error);
      throw error;
    } finally {
      self.uploadingCount = Math.max(0, self.uploadingCount - 1);
    }
  }),

  // Detach an external file from this thread and delete it server-side.
  removeFile: flow(function* removeFile(fileId: string) {
    try {
      const { data } = yield api.noesis.deleteThreadFile(getIdentifier(self)!, fileId);
      self.attachedFiles.replace(data.files || []);
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  })
});

export default actions;
